import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  FileText, Vote, Loader2, ExternalLink, ThumbsUp, ThumbsDown, Minus, Shield, Info,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VoteBadge = ({ voto }) => {
  const v = (voto || '').toLowerCase();
  if (v.includes('sim')) return <Badge className="bg-[#2E7D32] text-white text-[10px]"><ThumbsUp className="w-3 h-3 mr-1" />Sim</Badge>;
  if (v.includes('não') || v.includes('nao')) return <Badge className="bg-[#C62828] text-white text-[10px]"><ThumbsDown className="w-3 h-3 mr-1" />Não</Badge>;
  if (v.includes('abst')) return <Badge className="bg-[#F57F17] text-white text-[10px]"><Minus className="w-3 h-3 mr-1" />Abstenção</Badge>;
  return <Badge className="bg-[#475569] text-white text-[10px]">{voto || 'n/d'}</Badge>;
};

const LegislativeActivity = ({ deputadoId }) => {
  const [proposicoes, setProposicoes] = useState(null);
  const [votacoes, setVotacoes] = useState(null);
  const [loadingProps, setLoadingProps] = useState(true);
  const [loadingVotos, setLoadingVotos] = useState(true);

  useEffect(() => {
    if (!deputadoId) return;
    let cancelled = false;
    setLoadingProps(true);
    axios.get(`${API}/legislative/deputado/${deputadoId}/proposicoes`, { params: { limit: 20 }, timeout: 60000 })
      .then(r => { if (!cancelled) setProposicoes(r.data); })
      .catch(() => { if (!cancelled) setProposicoes({ proposicoes: [], error: true }); })
      .finally(() => { if (!cancelled) setLoadingProps(false); });

    setLoadingVotos(true);
    axios.get(`${API}/legislative/deputado/${deputadoId}/votacoes`, { params: { limit: 10 }, timeout: 90000 })
      .then(r => { if (!cancelled) setVotacoes(r.data); })
      .catch(() => { if (!cancelled) setVotacoes({ votacoes: [], error: true }); })
      .finally(() => { if (!cancelled) setLoadingVotos(false); });
    return () => { cancelled = true; };
  }, [deputadoId]);

  return (
    <div className="space-y-4" data-testid="legislative-activity">
      {/* Cabeçalho com fonte */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-base font-bold text-[#0F1C2E] flex items-center gap-2">
          <Vote className="w-4 h-4 text-[#006030]" />
          Atuação Legislativa
        </h3>
        <Badge className="bg-[#2E7D32] text-white text-[10px]">
          <Shield className="w-3 h-3 mr-1" /> Dados oficiais
        </Badge>
      </div>

      {/* Proposições */}
      <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md" data-testid="section-proposicoes">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-[#0F1C2E] flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#1565C0]" /> Proposições recentes de autoria
          </h4>
          {proposicoes && !loadingProps && (
            <Badge variant="outline" className="text-[10px]">{proposicoes.total || 0} registros</Badge>
          )}
        </div>

        {loadingProps && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-[#1565C0] mr-2" />
            <span className="text-xs text-[#94A3B8]">Carregando proposições...</span>
          </div>
        )}

        {!loadingProps && (!proposicoes?.proposicoes || proposicoes.proposicoes.length === 0) && (
          <p className="text-xs text-[#94A3B8] italic py-2">
            Nenhuma proposição de autoria registrada no período consultado.
          </p>
        )}

        {!loadingProps && proposicoes?.proposicoes?.length > 0 && (
          <ul className="divide-y divide-[#E2E8F0]">
            {proposicoes.proposicoes.map((p, idx) => (
              <li key={p.id || idx} className="py-2.5" data-testid={`proposicao-${idx}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-[#1565C0] text-white text-[10px]">{p.titulo}</Badge>
                      <Badge variant="outline" className="text-[9px] text-[#475569]">{p.situacao}</Badge>
                      {p.data_apresentacao && (
                        <span className="text-[10px] text-[#94A3B8]">
                          {new Date(p.data_apresentacao).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#475569] mt-1 line-clamp-2">
                      {p.ementa || <em className="text-[#94A3B8]">Sem ementa registrada.</em>}
                    </p>
                  </div>
                  <a
                    href={p.url_oficial}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] text-[#006030] hover:underline flex items-center gap-0.5 shrink-0"
                  >
                    Ver <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </li>
            ))}
          </ul>
        )}

        {proposicoes?.fonte && (
          <p className="text-[9px] text-[#94A3B8] mt-2 flex items-center gap-1">
            <Info className="w-3 h-3" /> Fonte: {proposicoes.fonte}
          </p>
        )}
      </Card>

      {/* Votações */}
      <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md" data-testid="section-votacoes">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-[#0F1C2E] flex items-center gap-2">
            <Vote className="w-4 h-4 text-[#2E7D32]" /> Como votou (votações nominais recentes)
          </h4>
          {votacoes?.resumo && !loadingVotos && (
            <div className="flex gap-1">
              <Badge className="bg-[#2E7D32] text-white text-[9px]">Sim: {votacoes.resumo.sim}</Badge>
              <Badge className="bg-[#C62828] text-white text-[9px]">Não: {votacoes.resumo.nao}</Badge>
              {votacoes.resumo.abstencao > 0 && (
                <Badge className="bg-[#F57F17] text-white text-[9px]">Abst: {votacoes.resumo.abstencao}</Badge>
              )}
            </div>
          )}
        </div>

        {loadingVotos && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-[#2E7D32] mr-2" />
            <span className="text-xs text-[#94A3B8]">Carregando votações (pode levar alguns segundos)...</span>
          </div>
        )}

        {!loadingVotos && (!votacoes?.votacoes || votacoes.votacoes.length === 0) && (
          <p className="text-xs text-[#94A3B8] italic py-2">
            Nenhuma votação nominal recente registrada para este parlamentar.
          </p>
        )}

        {!loadingVotos && votacoes?.votacoes?.length > 0 && (
          <ul className="divide-y divide-[#E2E8F0]">
            {votacoes.votacoes.map((v, idx) => (
              <li key={v.votacao_id || idx} className="py-2.5" data-testid={`votacao-${idx}`}>
                <div className="flex items-start gap-3">
                  <VoteBadge voto={v.voto} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#0F1C2E]">
                      {v.proposicao_titulo || v.sigla_orgao || 'Votação'}
                      {v.data && <span className="text-[10px] text-[#94A3B8] ml-2">{new Date(v.data).toLocaleDateString('pt-BR')}</span>}
                    </p>
                    <p className="text-[11px] text-[#475569] line-clamp-2 mt-0.5">
                      {v.descricao || <em className="text-[#94A3B8]">Sem descrição registrada.</em>}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {votacoes?.fonte && (
          <p className="text-[9px] text-[#94A3B8] mt-2 flex items-center gap-1">
            <Info className="w-3 h-3" /> Fonte: {votacoes.fonte}
            {votacoes.cached && <span className="italic"> · (cache)</span>}
          </p>
        )}
      </Card>

      {/* Nota explicativa */}
      <div className="p-3 bg-[#F4F6F8] border border-[#E2E8F0] rounded-md text-[10px] text-[#475569]">
        <p>
          <strong>Como ler estes dados:</strong> as proposições mostram projetos de lei, requerimentos e outras matérias
          apresentadas pelo parlamentar. As votações nominais registram o voto individual em matérias do plenário.
          Os dados são obtidos diretamente da Câmara dos Deputados, Dados Abertos, sem interpretação partidária.
        </p>
      </div>
    </div>
  );
};

export default LegislativeActivity;
